import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart, CartLink } from "@/context/CartContext";
import Navbar from "@/components/Navbar";
import { toast } from "@/lib/toast";
import { explodeSwagItems } from "@/lib/swag";
import { Loader2, ShoppingBag, Minus, Plus, ChevronLeft, Shirt, Share2 } from "lucide-react";
import { PreorderRibbon } from "@/components/PreorderRibbon";

const fmtPrice = (c) =>
  c == null ? "Price soon" : `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const inputCls = "w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground transition-colors font-body text-sm"; // eslint-disable-line no-unused-vars
const labelCls = "font-mono text-[10px] uppercase tracking-wide text-secondary-foreground block mb-1";

export default function SwagProduct() {
  const { slug } = useParams();
  const cart = useCart();
  const [cards, setCards] = useState(null); // null = loading
  const [qty, setQty] = useState(1);
  const [selSize, setSelSize] = useState("");

  useEffect(() => {
    api.get("/swag").then(({ data }) => setCards(explodeSwagItems(data.items || []))).catch(() => setCards([]));
  }, []);

  const card = useMemo(() => (cards || []).find((c) => c.slug === slug) || null, [cards, slug]);
  const item = card?.item || null;
  const siblings = useMemo(
    () => (!item ? [] : (cards || []).filter((c) => c.item.item_id === item.item_id)),
    [cards, item]
  );
  // "You might also like" = every other item's card (exclude the whole current item, both colorways —
  // the color switcher already covers Olive/Black).
  const related = useMemo(
    () => (!item ? [] : (cards || []).filter((c) => c.item.item_id !== item.item_id)),
    [cards, item]
  );

  const loading = cards === null;
  const hasSizes = (item?.sizes || []).length > 0;
  const soldOut = item ? !item.in_stock : false;
  const forSale = item ? item.price_cents != null && item.price_cents > 0 : false;
  const purchasable = !soldOut && forSale;
  const maxQty = !item ? 1 : item.preorder ? 25 : item.stock == null ? 25 : Math.max(1, Math.min(25, item.stock));
  const totalCents = (item?.price_cents || 0) * qty;

  const addToCart = () => {
    if (!purchasable) return;
    if (hasSizes && !selSize) { toast.error("Choose a size."); return; }
    cart.add({
      item_id: item.item_id,
      item_name: item.name,
      slug: card.slug,
      color: card.colorName || null,
      size: hasSizes ? selSize : null,
      unit_price_cents: item.price_cents,
      image: card.image,
      preorder: !!item.preorder,
      maxQty,
      quantity: qty,
    });
    toast.success(`Added to cart — ${qty}× ${card.title}.`);
  };

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <main className="max-w-[1200px] mx-auto px-4 pt-8 pb-24" data-testid="swag-product-page">
        <div className="flex items-center justify-between mb-8">
          <Link to="/swag" data-testid="swag-product-back" className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-wide text-secondary-foreground hover:text-olive-bright transition-colors">
            <ChevronLeft className="w-3.5 h-3.5" /> Swag
          </Link>
          <div className="flex items-center gap-4">
            {card && (
              <button
                type="button"
                data-testid="swag-product-share"
                onClick={async () => {
                  const shareUrl = `${window.location.origin}/api/share/swag/${card.slug}`;
                  try {
                    if (navigator.share) { await navigator.share({ title: card.title, url: shareUrl }); return; }
                    await navigator.clipboard.writeText(shareUrl);
                    toast.success("Share link copied — it shows a preview with the photo, name and price.");
                  } catch { /* user cancelled share */ }
                }}
                className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wide text-secondary-foreground hover:text-olive-bright transition-colors"
              >
                <Share2 className="w-3.5 h-3.5" /> Share
              </button>
            )}
            <CartLink />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground font-mono text-sm uppercase" data-testid="swag-product-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : !card ? (
          <div className="py-28 flex flex-col items-center justify-center text-center" data-testid="swag-product-notfound">
            <Shirt className="w-9 h-9 text-muted-foreground mb-4" />
            <p className="font-mono uppercase tracking-[0.14em] text-secondary-foreground text-base">This item isn't available.</p>
            <Link to="/swag" className="mt-5 font-mono text-[11px] uppercase tracking-wide text-olive-bright hover:underline">← Back to Swag</Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            {/* Gallery */}
            <div className="border border-sniper bg-surface overflow-hidden">
              <div className="aspect-square bg-obsidian flex items-center justify-center relative">
                {card.image ? (
                  <img src={card.image} alt={card.title} className="w-full h-full object-contain" data-testid="swag-product-image" />
                ) : (
                  <Shirt className="w-14 h-14 text-muted-foreground" />
                )}
                {item.preorder && !soldOut && (
                  <PreorderRibbon size="md" testid="swag-product-preorder" />
                )}
                {soldOut && (
                  <span className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-wide bg-obsidian/80 border border-blaze/60 text-blaze px-2 py-1" data-testid="swag-product-soldout">Sold out</span>
                )}
              </div>
            </div>

            {/* Details + order */}
            <div className="space-y-6">
              <div>
                {item.category && <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-2">{item.category}</p>}
                <h1 className="font-heading uppercase tracking-tight text-3xl sm:text-4xl text-foreground leading-tight" data-testid="swag-product-title">{card.title}</h1>
                <p className="font-heading text-2xl text-olive-bright mt-3" data-testid="swag-product-price">{fmtPrice(item.price_cents)}</p>
              </div>

              {/* Other colorways → sibling product pages */}
              {siblings.length > 1 && (
                <div>
                  <span className={labelCls}>Color{card.colorName ? <span className="text-secondary-foreground normal-case"> · {card.colorName}</span> : null}</span>
                  <div className="flex flex-wrap gap-2" data-testid="swag-product-colors">
                    {siblings.map((s) => {
                      const on = s.slug === card.slug;
                      return (
                        <Link key={s.slug} to={`/swag/${s.slug}`} data-testid={`swag-product-color-${s.colorName}`}
                          className={`inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wide px-3 py-1.5 border transition-colors ${on ? "border-olive-bright bg-olive/20 text-foreground" : "border-sniper text-muted-foreground hover:text-foreground hover:border-olive/60"}`}>
                          {s.image ? <img src={s.image} alt={s.colorName} className="w-4 h-4 object-cover border border-white/20" /> : null}
                          {s.colorName || "Default"}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}

              {hasSizes && (
                <div>
                  <span className={labelCls}>Size{selSize ? <span className="text-secondary-foreground normal-case"> · {selSize}</span> : null}</span>
                  <div className="flex flex-wrap gap-2" data-testid="swag-product-sizes">
                    {item.sizes.map((s) => (
                      <button type="button" key={s} onClick={() => setSelSize(s)} data-testid={`swag-product-size-${s}`}
                        className={`font-mono text-[11px] uppercase tracking-wide min-w-[46px] px-2.5 py-2 border transition-colors ${selSize === s ? "border-olive-bright bg-olive/20 text-foreground" : "border-sniper text-muted-foreground hover:text-foreground hover:border-olive/60"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between max-w-[260px]">
                <span className={labelCls} style={{ marginBottom: 0 }}>Quantity</span>
                <div className="flex items-center border border-sniper">
                  <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-2 text-foreground hover:bg-olive/10" data-testid="swag-product-qty-dec"><Minus className="w-3.5 h-3.5" /></button>
                  <span className="px-4 font-mono text-sm tabular-nums text-foreground" data-testid="swag-product-qty">{qty}</span>
                  <button type="button" onClick={() => setQty((q) => Math.min(maxQty, q + 1))} className="px-3 py-2 text-foreground hover:bg-olive/10" data-testid="swag-product-qty-inc"><Plus className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              {item.stock != null && (
                <p className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground -mt-3">{item.stock} available</p>
              )}

              <div className="border-t border-sniper pt-6 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[11px] uppercase tracking-wide text-secondary-foreground">Total</span>
                  <span className="font-heading text-xl text-olive-bright" data-testid="swag-product-total">{fmtPrice(totalCents)}</span>
                </div>
                <button type="button" onClick={addToCart} disabled={!purchasable} data-testid="swag-product-add"
                  className="w-full bg-olive hover:bg-olive-bright disabled:opacity-50 disabled:cursor-not-allowed text-obsidian font-mono uppercase text-xs tracking-wide px-4 py-3 transition-colors inline-flex items-center justify-center gap-2">
                  {soldOut ? "Sold out" : !forSale ? "Price soon" : (<><ShoppingBag className="w-4 h-4" /> Add to cart</>)}
                </button>
                {cart?.count > 0 && (
                  <Link to="/swag/cart" data-testid="swag-product-viewcart" className="block text-center font-mono text-[11px] uppercase tracking-wide text-olive-bright hover:underline">
                    View cart ({cart.count}) →
                  </Link>
                )}
                <p className="font-mono text-[10px] text-center text-muted-foreground">
                  {item.preorder ? "Pre-order — charged today at checkout, ships when available." : "First-party gear shipped by OpticSwap. Secure checkout via Stripe."}
                </p>
              </div>
            </div>
          </div>
        )}

        {card && item.description && (
          <p className="mt-14 max-w-2xl font-body text-sm text-secondary-foreground leading-relaxed" data-testid="swag-product-description">{item.description}</p>
        )}

        {card && related.length > 0 && (
          <div className="mt-16 pt-10" style={{ borderTop: "1px solid #2A2C20" }} data-testid="swag-related">
            <p className="font-mono text-[11px] tracking-[0.14em] uppercase mb-5" data-testid="swag-related-label">
              <span style={{ color: "#3E3F42" }}>// </span>
              <span style={{ color: "#748A3D" }}>Pairs well with</span>
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {related.map((c) => (
                <Link key={c.key} to={`/swag/${c.slug}`} data-testid={`swag-related-${c.slug}`}
                  className="group border border-sniper bg-surface overflow-hidden flex flex-col transition-colors hover:border-olive focus:outline-none focus-visible:border-olive-bright">
                  <div className="aspect-square bg-obsidian overflow-hidden flex items-center justify-center">
                    {c.image
                      ? <img src={c.image} alt={c.title} className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105" />
                      : <Shirt className="w-7 h-7 text-muted-foreground" />}
                  </div>
                  <div className="p-3 flex items-center justify-between gap-2">
                    <span className="font-heading uppercase tracking-tight text-foreground text-sm leading-tight truncate">{c.title}</span>
                    <span className="shrink-0 font-heading text-olive-bright text-sm">{fmtPrice(c.item.price_cents)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {card && (
          <div className="mt-16 pt-10 flex justify-center" style={{ borderTop: "1px solid #2A2C20" }} data-testid="elev850-credit">
            <div className="inline-flex items-center gap-4 px-5 py-4" style={{ backgroundColor: "#14150F", border: "1px solid #2A2C20", borderRadius: "14px" }}>
              <span className="block w-16 h-16 rounded-full overflow-hidden shrink-0" style={{ boxShadow: "0 0 0 1px #2A2C20, 0 6px 22px rgba(0,0,0,0.45)" }}>
                <img src="/brand/elev850-badge.png" alt="Elev850 Gulf Coast" className="w-full h-full object-cover" data-testid="elev850-logo" />
              </span>
              <span className="flex flex-col leading-none" style={{ fontFamily: '"IBM Plex Mono", "JetBrains Mono", monospace' }}>
                <span className="text-[10px] uppercase tracking-[0.24em] mb-2" style={{ color: "#8FA05C" }}>Powered by</span>
                <span className="text-xl uppercase tracking-[0.12em] font-semibold" style={{ color: "#E8C46A" }}>Elev850</span>
              </span>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
